// Rotating status words while the assistant works — "Analysiere …",
// "Verknüpfe Informationen …" — instead of one fixed "Denke nach …". The words come
// from the locale (`chatMessages.thinkingWords`); without a list the fixed
// label stays, so a missing translation never shows a key.

import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ThinkingShimmer } from "@/components/agents/loading-states/thinking-shimmer";
import { EASE_IN_OUT } from "@/lib/ease";
import { cn } from "@/lib/utils";

/** "legal.search_regulations …" → "legal.search_regulations": the step label without its ellipsis, never cut. */
export function toolLabel(step: string | undefined): string {
  return (step ?? "").replace(/\s*…\s*$|\s*\.\.\.\s*$/, "").trim();
}

/** Calm enough to read as a status, not a ticker: two shimmer passes per word. */
const WORD_MS = 6000;
/** Slow, symmetric ease for the swap — a spring reads as a jolt on a status line. */
const SWAP_TRANSITION = { duration: 0.7, ease: EASE_IN_OUT };

export interface ThinkingWordsProps {
  /** Shown when the locale has no word list. */
  fallback: string;
  /**
   * Name of the step running right now (tool or step label). Every other word
   * then names it — "Nutze bpm.list-models …" — and a new step shows its
   * sentence at once.
   */
  tool?: string;
  className?: string;
}

function shuffled(words: string[]) {
  const copy = [...words];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

export function ThinkingWords({ fallback, tool, className }: ThinkingWordsProps) {
  const { t } = useTranslation();
  const reduce = useReducedMotion() ?? false;
  const list = t("chatMessages.thinkingWords", { returnObjects: true }) as unknown;
  const valid =
    Array.isArray(list) && list.length > 0 && list.every((word) => typeof word === "string");
  // `t` returns a new array on every render; its content is the stable key.
  const listKey = valid ? (list as string[]).join("\u0000") : "";
  // A fresh order per mount (per answer) and per language, so two answers in a
  // row do not start with the same word.
  const words = useMemo(
    () => (listKey ? shuffled(listKey.split("\u0000")) : []),
    [listKey],
  );
  const toolName = toolLabel(tool);
  // Interpolated by i18next itself; `{{tool}}` in the locale.
  const templateList = toolName
    ? (t("chatMessages.thinkingToolWords", { returnObjects: true, tool: toolName }) as unknown)
    : [];
  const templates =
    Array.isArray(templateList) && templateList.every((entry) => typeof entry === "string")
      ? (templateList as string[])
      : [];
  const [index, setIndex] = useState(0);

  // A new step starts the sequence over, on the sentence that names it.
  useEffect(() => {
    setIndex(0);
  }, [toolName]);

  useEffect(() => {
    if (words.length < 2 || reduce) return;
    const timer = window.setInterval(() => setIndex((current) => current + 1), WORD_MS);
    return () => window.clearInterval(timer);
  }, [words.length, reduce]);

  // Even slots name the running step, odd ones are plain words.
  const namesTool = Boolean(toolName) && templates.length > 0 && index % 2 === 0;
  const word = !words.length
    ? ""
    : namesTool
      ? templates[(index / 2) % templates.length]
      : words[Math.floor(index / (toolName && templates.length ? 2 : 1)) % words.length];
  // The row after the word (dots, chevron) follows its width. Measured from a
  // hidden copy so the wrapper can glide to the new width instead of snapping.
  const measureRef = useRef<HTMLSpanElement>(null);
  const [width, setWidth] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (measureRef.current) setWidth(measureRef.current.offsetWidth);
  }, [word]);

  if (words.length === 0) {
    return <ThinkingShimmer className={className}>{fallback}</ThinkingShimmer>;
  }

  return (
    // The words are hidden from assistive tech and the status carries the
    // fixed label: a screen reader should hear that work is going on, not a
    // new word every few seconds.
    // Flex all the way down, like the bare `ThinkingShimmer` it replaces: an
    // inline box here opens a line box with its own descender space and the
    // word sits lower than the dot beside it.
    <motion.span
      className="relative flex items-center overflow-hidden"
      aria-label={fallback}
      role="status"
      initial={false}
      animate={width === null ? undefined : { width }}
      transition={reduce ? { duration: 0 } : SWAP_TRANSITION}
    >
      <span
        ref={measureRef}
        aria-hidden="true"
        className={cn("invisible absolute left-0 whitespace-nowrap font-medium", className)}
      >
        {word}
      </span>
      {/* Crossfade: the old word drifts up and blurs out while the new one
          settles in from below, both at once — no gap, no bounce. */}
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={word}
          aria-hidden="true"
          initial={reduce ? { opacity: 0 } : { opacity: 0, y: 6, filter: "blur(4px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, y: -6, filter: "blur(4px)" }}
          transition={reduce ? { duration: 0.2 } : SWAP_TRANSITION}
          className="flex items-center whitespace-nowrap"
        >
          <ThinkingShimmer className={cn(className)}>{word}</ThinkingShimmer>
        </motion.span>
      </AnimatePresence>
    </motion.span>
  );
}
